// src/DiagramsNetEditor.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { DrawIoEmbed } from "react-drawio";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import "./styles.css";

// Diagrams.net editor configuration
const DRAWIO_CONFIG = {
  ui: 'kennedy',        // Modern interface theme
  spin: true,          // Show loading spinner
  libraries: true,     // Enable shape libraries
  saveAndExit: false,  // Disable built-in save button
  noSaveBtn: true,     // Hide save button
  noExitBtn: true,     // Hide exit button
  enableAi: true,
};

// Minimum XML length to consider a diagram as "non-empty"
// A completely empty diagram still has XML structure (~50-80 chars), 
// so we use 100 as threshold to ensure there's actual content
const MIN_DIAGRAM_XML_LENGTH = 100;

// PNG export configuration
const PNG_EXPORT_DELAY = 1500; // Wait for editor stabilization before export
const PNG_EXPORT_TIMEOUT = 5000; // Max wait time for PNG export callback (5 seconds)
const PNG_EXPORT_MAX_ATTEMPTS = 3;

/**
 * Validates if PNG data is legitimate
 * Accepts both data URI format and raw base64
 * NO arbitrary size limits - small diagrams are valid
 */
const isValidPNG = (data) => {
  if (typeof data !== "string" || !data || data.length < 50) {
    return false;
  }
  
  // Accept data URI format
  if (data.startsWith("data:image/png;base64,") || data.startsWith("data:image/")) {
    return true;
  }
  
  // Accept raw base64 - check for PNG signature (iVBORw0KGgo)
  if (data.startsWith("iVBORw0KGgo")) {
    return true;
  }
  
  // If it looks like base64, accept it (trust diagrams.net export)
  if (/^[A-Za-z0-9+/=]+$/.test(data.substring(0, Math.min(100, data.length)))) {
    return true;
  }
  
  return false;
};

// HTML escape function to prevent XSS
const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * DiagramsNetEditor Component
 * 
 * Integrates diagrams.net (draw.io) editor for creating architecture diagrams.
 * Replaces the custom SingleLineDiagram implementation with the full-featured
 * diagrams.net interface while maintaining existing features like save/load,
 * AI description generation, and proposal integration.
 */
export default function DiagramsNetEditor({ tenderId, tender, costingItems }) {
  // Editor reference
  const drawioRef = useRef(null);
  
  // State
  const [diagramXml, setDiagramXml] = useState(null);
  const [architectureName, setArchitectureName] = useState("System Architecture");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [systemDescription, setSystemDescription] = useState("");
  const [showSystemDescription, setShowSystemDescription] = useState(false);
  const [exportedData, setExportedData] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  
  // PNG export state
  const [pngExportStatus, setPngExportStatus] = useState(""); // "", "generating", "success", "failed"
  const [pngExportAttempts, setPngExportAttempts] = useState(0);
  
  // Ref to track save timeout
  const saveTimeoutRef = useRef(null);
  
  // Print options state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    includeDiagram: true,
    includeDescription: false,
  });

  // Load saved diagram data
  // Load saved diagram data
  useEffect(() => {
    const loadDiagram = async () => {
      if (!tenderId) return;
      
      try {
        setLoading(true);
        // Debug logging - can be removed or controlled via environment variable in production
        console.log("Loading diagram for tender:", tenderId);
        const sldRef = doc(db, "tenders", tenderId, "sld", "main");
        const sldSnap = await getDoc(sldRef);
        
        if (sldSnap.exists()) {
          const data = sldSnap.data();
          console.log("Diagram data found in Firestore");
          console.log("Diagram XML length:", data.diagramXml ? data.diagramXml.length : 0);
          console.log("Architecture name:", data.architectureName);
          setDiagramXml(data.diagramXml || null);
          setSystemDescription(data.systemDescription || "");
          setArchitectureName(data.architectureName || "System Architecture");
        } else {
          console.log("No diagram data found in Firestore for this tender");
        }
      } catch (error) {
        console.error("Error loading diagram:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadDiagram();
  }, [tenderId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Save diagram data
  const saveDiagram = useCallback(async () => {
    if (!tenderId) return;
    
    if (!editorReady) {
      setStatusMessage("⚠️ Editor is still loading. Please wait...");
      setTimeout(() => setStatusMessage(""), 3000);
      return;
    }
    
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setSaving(true);
    setStatusMessage("💾 Exporting diagram...");
    
    try {
      // Export current diagram from editor
      if (drawioRef.current) {
        console.log("Calling exportDiagram on editor with format: xmlpng");
        // Use xmlpng format which is most reliable for getting diagram XML
        // This format returns SVG with embedded mxfile XML
        drawioRef.current.exportDiagram({ 
          format: 'xmlpng',
          // Add additional parameters
          allPages: false,
          currentPage: true
        }, (data) => {
          // Wrap callback to match handleExport signature
          handleExport({ format: 'xmlpng', data: data });
        });
        
        // Set a timeout to reset saving state if export doesn't complete
        saveTimeoutRef.current = setTimeout(() => {
          console.log("Export timeout - resetting save state");
          setSaving(false);
          setStatusMessage("⚠️ Save timeout. The diagram might be empty or the editor is not responding. Please try again.");
          setTimeout(() => setStatusMessage(""), 5000);
        }, 10000); // 10 second timeout
      } else {
        setSaving(false);
        setStatusMessage("❌ Editor not initialized");
        setTimeout(() => setStatusMessage(""), 3000);
      }
      
      // Wait for export to complete (handled by onExport callback)
      // The actual save will happen in handleExport
    } catch (error) {
      console.error("Error saving diagram:", error);
      setStatusMessage("❌ Error saving diagram: " + error.message);
      setTimeout(() => setStatusMessage(""), 3000);
      setSaving(false);
    }
  }, [tenderId, editorReady]);

  // Handle editor load
  const handleEditorLoad = useCallback(() => {
    console.log("Diagrams.net editor loaded");
    setEditorReady(true);
  }, []);

  // Helper function to extract pure diagram XML from various export formats
  const extractPureDiagramXml = useCallback((data, format) => {
    let diagramXml = data;
    
    // Special handling for xmlpng format - diagrams.net can load PNG with embedded XML directly
    if (format === 'xmlpng' && (data.startsWith('data:image/png') || data.startsWith('data:image/'))) {
      console.log("✅ Received XMLPNG format (PNG with embedded diagram XML)");
      console.log("XMLPNG can be loaded directly by diagrams.net - saving as-is");
      console.log("Data length:", data.length);
      console.log("Data preview (first 100 chars):", data.substring(0, 100));
      
      // The XMLPNG format is a PNG image with the diagram XML embedded in it
      // diagrams.net can load this format directly, so we don't need to extract the XML
      // Just return the PNG data as-is
      return data;
    }
    
    // Reject PNG data for non-xmlpng formats - it's not valid diagram XML
    if (data.startsWith('data:image/png') || data.startsWith('data:image/')) {
      console.error("❌ Received image data instead of XML - cannot save as diagram");
      console.log("Data type detected: image/png or similar");
      return null;
    }
    
    if (format === 'xml' || format === 'svg' || format === 'xmlsvg' || format === 'xmlpng') {
      console.log("Processing", format.toUpperCase(), "format");
      
      try {
        // For XMLPNG format, the data is PNG with embedded XML - skip XML validation
        if (format === 'xmlpng' && diagramXml.startsWith('data:image/png')) {
          console.log("✅ XMLPNG format validated - contains PNG with embedded diagram data");
          return diagramXml;
        }
        
        // For other formats, check if data contains valid diagram XML
        if (!diagramXml.includes('<mxfile') && !diagramXml.includes('<mxGraphModel')) {
          console.error("Data does not contain valid mxfile/mxGraphModel XML");
          console.log("Data preview:", diagramXml.substring(0, 200));
          return null; // Return null instead of invalid data
        }
        
        console.log("✅ Data contains valid mxfile/mxGraphModel XML");
        
        // If wrapped in SVG, try extraction methods
        if (diagramXml.includes('<svg')) {
          console.log("Data is wrapped in SVG, extracting...");
          
          // Method 1: Extract from SVG content attribute
          const contentMatch = diagramXml.match(/content="([^"]+)"/);
          if (contentMatch && contentMatch[1]) {
            try {
              const decodedContent = decodeURIComponent(contentMatch[1]);
              if (decodedContent.includes('<mxfile') || decodedContent.includes('<mxGraphModel')) {
                console.log("✅ Extracted and decoded XML from SVG content attribute");
                diagramXml = decodedContent;
                return diagramXml;
              }
            } catch (e) {
              console.warn("Could not decode content attribute:", e);
            }
          }
          
          // Method 2: Extract mxfile section from SVG
          const mxfileMatch = diagramXml.match(/(<mxfile[^>]*>[\s\S]*?<\/mxfile>)/);
          if (mxfileMatch && mxfileMatch[1]) {
            console.log("✅ Extracted mxfile from SVG wrapper");
            diagramXml = mxfileMatch[1];
            return diagramXml;
          }
          
          console.warn("Could not extract XML from SVG wrapper");
        }
      } catch (e) {
        console.error("Error processing diagram data:", e);
        return null;
      }
    }
    
    return diagramXml;
  }, []);

  // Helper function to save diagram data to Firestore
  const saveDiagramToFirestore = useCallback(async (diagramData) => {
    setStatusMessage("💾 Saving to database...");
    // Debug logging - can be removed or controlled via environment variable in production
    console.log("Saving diagram to Firestore");
    console.log("Tender ID:", tenderId);
    console.log("Diagram XML length:", diagramData ? diagramData.length : 0);
    console.log("Architecture name:", architectureName);
    
    try {
      const sldRef = doc(db, "tenders", tenderId, "sld", "main");
      const dataToSave = {
        diagramXml: diagramData,
        systemDescription,
        architectureName,
        lastModified: new Date().toISOString(),
      };
      
      console.log("Data to save:", {
        diagramXmlLength: dataToSave.diagramXml ? dataToSave.diagramXml.length : 0,
        architectureName: dataToSave.architectureName,
        hasSystemDescription: !!dataToSave.systemDescription,
      });
      
      await setDoc(sldRef, dataToSave, { merge: true });
      
      console.log("Diagram saved successfully to Firestore");
      setStatusMessage("✅ Diagram saved successfully!");
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      setStatusMessage(`❌ Error saving diagram: ${error.message || 'Unknown error'}`);
      setTimeout(() => setStatusMessage(""), 4000);
    } finally {
      setSaving(false);
    }
  }, [tenderId, systemDescription, architectureName]);

  // Handle export from diagrams.net
  const handleExport = useCallback(async (data) => {
    console.log("Export callback triggered with format:", data.format);
    console.log("Export data type:", typeof data.data);
    console.log("Export data length:", data.data ? data.data.length : 0);
    
    // Handle PNG export for "Send to Proposal" feature or AI description
    if (data.format === 'png') {
      console.log("PNG export detected in onExport handler");
      if (window._pngExportResolver) {
        console.log("Calling PNG resolver with data");
        window._pngExportResolver(data.data);
      } else if (window._pngExportResolverAI) {
        console.log("Calling PNG resolver for AI with data");
        window._pngExportResolverAI(data.data);
      } else {
        console.warn("PNG export received but no resolver registered");
      }
      return; // Don't process PNG as save operation
    }
    
    // Clear the timeout since export completed
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // Handle XML, SVG, XMLSVG, or XMLPNG export (for saving)
    if (data.format === 'xml' || data.format === 'svg' || data.format === 'xmlsvg' || data.format === 'xmlpng') {
      // Log format received
      if (data.format !== 'xml') {
        console.log(`Received ${data.format.toUpperCase()} format (this is normal for react-drawio)`);
      }
      
      // Extract pure diagram XML from the export data
      const diagramData = extractPureDiagramXml(data.data, data.format);
      
      // Check if we have valid diagram data
      if (!diagramData) {
        console.error("❌ Failed to extract valid XML from export data");
        setStatusMessage("⚠️ Export format error. Please try saving again.");
        setSaving(false);
        setTimeout(() => setStatusMessage(""), 4000);
        return;
      }
      
      if (diagramData.length < MIN_DIAGRAM_XML_LENGTH) {
        console.error("Diagram data is too short:", diagramData.length);
        setStatusMessage("⚠️ Diagram appears to be empty. Please draw something first.");
        setSaving(false);
        setTimeout(() => setStatusMessage(""), 4000);
        return;
      }
      
      // Store the diagram data
      console.log("Storing diagram data for save");
      console.log("Final diagram data length:", diagramData.length);
      console.log("Data starts with:", diagramData.substring(0, 100));
      setDiagramXml(diagramData);
      setExportedData(diagramData);
      
      // Save to Firestore
      await saveDiagramToFirestore(diagramData);
    } 
    // Handle PNG export (for printing)
    else if (data.format === 'png') {
      setExportedData(data.data);
      setSaving(false);
    } 
    // Unknown format
    else {
      console.error("Unexpected export format:", data.format);
      setSaving(false);
      setStatusMessage(`❌ Unexpected export format: ${data.format}`);
      setTimeout(() => setStatusMessage(""), 4000);
    }
  }, [saveDiagramToFirestore]);

  // Generate AI System Description
  const generateSystemDescription = async () => {
    setIsGeneratingAI(true);
    setStatusMessage("🤖 Preparing diagram for analysis...");
    
    try {
      // First, ensure we have the latest diagram data
      if (!diagramXml) {
        setStatusMessage("⚠️ Please save your diagram first before generating description");
        setIsGeneratingAI(false);
        setTimeout(() => setStatusMessage(""), 3000);
        return;
      }
      
      if (!drawioRef.current) {
        setStatusMessage("⚠️ Editor not ready. Please try again.");
        setIsGeneratingAI(false);
        setTimeout(() => setStatusMessage(""), 3000);
        return;
      }
      
      setStatusMessage("🤖 Exporting diagram as image...");
      
      // Export diagram as PNG for better AI analysis
      let diagramImageData = "";
      
      const attemptPngExport = (attemptNumber) => {
        return new Promise((resolve) => {
          setStatusMessage(`🤖 Exporting diagram (attempt ${attemptNumber}/${PNG_EXPORT_MAX_ATTEMPTS})...`);
          console.log(`=== PNG EXPORT FOR AI ATTEMPT ${attemptNumber}/${PNG_EXPORT_MAX_ATTEMPTS} ===`);
          
          let settled = false;
          
          const timeoutId = setTimeout(() => {
            if (!settled) {
              console.warn(`⏱️ PNG export TIMEOUT after ${PNG_EXPORT_TIMEOUT}ms on attempt ${attemptNumber}`);
              settled = true;
              if (window._pngExportResolverAI) {
                delete window._pngExportResolverAI;
              }
              resolve({ success: false, data: null });
            }
          }, PNG_EXPORT_TIMEOUT);
          
          // Use a different global variable to avoid conflicts with handleSendToProposal
          window._pngExportResolverAI = (pngData) => {
            if (settled) {
              console.log("PNG resolver AI called but Promise already settled");
              return;
            }
            
            console.log("🔥 PNG DATA CAPTURED for AI - CLEARING TIMEOUT");
            clearTimeout(timeoutId);
            
            if (!pngData || typeof pngData !== 'string' || pngData.length < 50) {
              console.warn(`❌ Invalid PNG data: empty or too short`);
              settled = true;
              delete window._pngExportResolverAI;
              resolve({ success: false, data: null });
              return;
            }
            
            // Normalize PNG data - ensure data URI prefix
            let normalizedPng = pngData;
            
            if (!normalizedPng.startsWith("data:image/")) {
              console.log(`📝 Adding data URI prefix to raw base64 (${normalizedPng.length} chars)`);
              normalizedPng = `data:image/png;base64,${normalizedPng}`;
            }
            
            console.log(`✅ PNG ACCEPTED for AI - ${normalizedPng.length} chars`);
            settled = true;
            delete window._pngExportResolverAI;
            resolve({ success: true, data: normalizedPng });
          };
          
          setTimeout(() => {
            if (!drawioRef.current) {
              console.error("Editor ref lost");
              clearTimeout(timeoutId);
              if (!settled) {
                settled = true;
                delete window._pngExportResolverAI;
                resolve({ success: false, data: null });
              }
              return;
            }
            
            console.log("Calling exportDiagram({ format: 'png' }) for AI analysis");
            
            try {
              drawioRef.current.exportDiagram({ format: "png" });
              console.log("✅ exportDiagram called, waiting for onExport handler...");
            } catch (error) {
              console.error("❌ EXCEPTION in exportDiagram call:", error);
              clearTimeout(timeoutId);
              settled = true;
              delete window._pngExportResolverAI;
              resolve({ success: false, data: null });
            }
          }, PNG_EXPORT_DELAY);
        });
      };
      
      // Retry logic for PNG export
      for (let attempt = 1; attempt <= PNG_EXPORT_MAX_ATTEMPTS; attempt++) {
        const result = await attemptPngExport(attempt);
        
        if (result.success && result.data) {
          diagramImageData = result.data;
          console.log(`✅ PNG export for AI succeeded on attempt ${attempt}`);
          break;
        }
        
        console.warn(`❌ PNG export for AI attempt ${attempt} failed`);
        
        if (attempt < PNG_EXPORT_MAX_ATTEMPTS) {
          console.log(`Retrying PNG export for AI...`);
          await new Promise(r => setTimeout(r, 500));
        }
      }
      
      if (!diagramImageData) {
        console.error("❌ PNG export failed after all retries - falling back to XML");
        setStatusMessage("⚠️ Could not export image, using diagram data instead...");
      }
      
      setStatusMessage("🤖 AI is analyzing diagram...");
      
      const response = await fetch("/api/generate-system-description", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          diagramImage: diagramImageData, // Send PNG image
          diagramXml: diagramXml, // Fallback to XML if image fails
          costingItems: costingItems || [],
          projectName: tender?.title || tender?.clientName || "Project",
          clientName: tender?.clientName || "Customer",
        }),
      });
      
      if (!response.ok) {
        let errorMessage = "Failed to generate system description";
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setSystemDescription(data.systemDescription);
      setShowSystemDescription(true);
      setStatusMessage("✅ System description generated!");
      
      // Save the description to Firestore directly (without triggering diagram export)
      try {
        const sldRef = doc(db, "tenders", tenderId, "sld", "main");
        await setDoc(sldRef, {
          systemDescription: data.systemDescription,
          lastModified: new Date().toISOString(),
        }, { merge: true });
      } catch (saveError) {
        console.error("Error saving description:", saveError);
      }
      
    } catch (error) {
      console.error("Error generating system description:", error);
      const errorMsg = error.message.includes("Failed to fetch") 
        ? "❌ Network error: Unable to connect to AI service"
        : `❌ ${error.message}`;
      setStatusMessage(errorMsg);
    } finally {
      setIsGeneratingAI(false);
      setTimeout(() => setStatusMessage(""), 5000);
    }
  };

  // Send to Proposal function
  const handleSendToProposal = async () => {
    const STATUS_MESSAGE_TIMEOUT = 3000; // Consistent timeout for status messages
    
    try {
      // Use current diagram XML data (not exportedData which might contain PNG)
      const diagramData = diagramXml;
      
      if (!diagramData) {
        setStatusMessage("⚠️ Please create and save a diagram first");
        setTimeout(() => setStatusMessage(""), STATUS_MESSAGE_TIMEOUT);
        return;
      }
      
      if (!drawioRef.current) {
        console.error("❌ drawioRef.current is null - cannot export diagram");
        setStatusMessage("⚠️ Editor not ready. Please try again.");
        setTimeout(() => setStatusMessage(""), STATUS_MESSAGE_TIMEOUT);
        return;
      }
      
      // Export diagram as PNG with production-grade retry logic
      let diagramImageData = "";
      let exportStatus = "XML_ONLY";
      
      const attemptPngExport = (attemptNumber) => {
        return new Promise((resolve) => {
          setPngExportStatus("generating");
          setStatusMessage(`⏳ Generating diagram image (attempt ${attemptNumber}/${PNG_EXPORT_MAX_ATTEMPTS})...`);
          console.log(`=== PNG EXPORT ATTEMPT ${attemptNumber}/${PNG_EXPORT_MAX_ATTEMPTS} ===`);
          
          let settled = false;
          
          // CRITICAL: Timeout to prevent hanging (production-grade requirement)
          const timeoutId = setTimeout(() => {
            if (!settled) {
              console.warn(`⏱️ PNG export TIMEOUT after ${PNG_EXPORT_TIMEOUT}ms on attempt ${attemptNumber}`);
              settled = true;
              // Clean up temporary handler
              if (window._pngExportResolver) {
                delete window._pngExportResolver;
              }
              resolve({ success: false, data: null });
            }
          }, PNG_EXPORT_TIMEOUT);
          
          // Store the resolve function globally so handleExport can access it
          // This is the workaround for react-drawio's callback not firing
          window._pngExportResolver = (pngData) => {
            if (settled) {
              console.log("PNG resolver called but Promise already settled");
              return;
            }
            
            console.log("🔥 PNG DATA CAPTURED via onExport - CLEARING TIMEOUT");
            clearTimeout(timeoutId);
            console.log("✅ Timeout cleared successfully");
            
            if (!pngData || typeof pngData !== 'string' || pngData.length < 50) {
              console.warn(`❌ Invalid PNG data: empty or too short`);
              settled = true;
              delete window._pngExportResolver;
              resolve({ success: false, data: null });
              return;
            }
            
            // Normalize PNG data - ensure data URI prefix
            let normalizedPng = pngData;
            
            // If raw base64, add data URI prefix
            if (!normalizedPng.startsWith("data:image/png;base64,") && 
                !normalizedPng.startsWith("data:image/")) {
              console.log(`📝 Adding data URI prefix to raw base64 (${normalizedPng.length} chars)`);
              normalizedPng = `data:image/png;base64,${normalizedPng}`;
            }
            
            // Accept the PNG - trust diagrams.net export
            console.log(`✅ PNG ACCEPTED - ${normalizedPng.length} chars`);
            settled = true;
            delete window._pngExportResolver;
            resolve({ success: true, data: normalizedPng });
          };
          
          // Wait for editor stabilization before export (CRITICAL)
          setTimeout(() => {
            if (!drawioRef.current) {
              console.error("Editor ref lost");
              clearTimeout(timeoutId);
              if (!settled) {
                settled = true;
                delete window._pngExportResolver;
                resolve({ success: false, data: null });
              }
              return;
            }
            
            console.log("Calling exportDiagram({ format: 'png' }) - will be captured by onExport handler");
            
            try {
              // Call exportDiagram without callback - onExport handler will catch it
              drawioRef.current.exportDiagram({ format: "png" });
              console.log("✅ exportDiagram called, waiting for onExport handler...");
            } catch (error) {
              console.error("❌ EXCEPTION in exportDiagram call:", error);
              clearTimeout(timeoutId);
              settled = true;
              delete window._pngExportResolver;
              resolve({ success: false, data: null });
            }
          }, PNG_EXPORT_DELAY);
        });
      };
      
      // Retry logic
      for (let attempt = 1; attempt <= PNG_EXPORT_MAX_ATTEMPTS; attempt++) {
        setPngExportAttempts(attempt);
        const result = await attemptPngExport(attempt);
        
        if (result.success && result.data) {
          diagramImageData = result.data;
          exportStatus = "WITH_IMAGE";
          setPngExportStatus("success");
          console.log(`✅ PNG export succeeded on attempt ${attempt}`);
          break;
        }
        
        console.warn(`❌ PNG export attempt ${attempt} failed`);
        
        if (attempt < PNG_EXPORT_MAX_ATTEMPTS) {
          console.log(`Retrying PNG export...`);
          await new Promise(r => setTimeout(r, 500)); // Brief pause between retries
        }
      }
      
      if (!diagramImageData) {
        console.error("❌ PNG export failed after all retries");
        setPngExportStatus("failed");
        setStatusMessage("⚠️ Image generation failed - saving with XML only");
        await new Promise(r => setTimeout(r, 2000));
      }
      
      setStatusMessage("📤 Sending to Proposal Writer...");
      
      // Save diagram XML, PNG image, and description to proposal collection
      // Store status explicitly (deterministic model)
      const proposalSldRef = doc(db, "tenders", tenderId, "proposal", "sld_data");
      const dataToSave = {
        diagramXml: diagramData, // XML format for editing
        diagramImage: diagramImageData || "", // PNG format for display in proposal
        exportStatus, // "XML_ONLY" | "WITH_IMAGE" - explicit status
        systemDescription, // AI-generated description
        architectureName, // Name of the architecture
        exportedAt: new Date().toISOString(),
        timestamp: new Date().toISOString(), // For SimpleProposalGenerator
        pngExportAttempts: pngExportAttempts, // Track retry attempts
      };
      
      await setDoc(proposalSldRef, dataToSave, { merge: true });
      
      console.log("✅ ====== DATA SENT TO PROPOSAL ======");
      console.log("Diagram XML length:", diagramData ? diagramData.length : 0);
      console.log("Diagram image status:", diagramImageData ? `✅ Present (${diagramImageData.length} chars, starts with: ${diagramImageData.substring(0, 50)}...)` : "❌ MISSING");
      console.log("System description:", systemDescription ? `✅ Present (${systemDescription.length} chars)` : "❌ None");
      console.log("Architecture name:", architectureName || "❌ None");
      console.log("Data saved to path: tenders/" + tenderId + "/proposal/sld_data");
      console.log("====================================");
      
      setStatusMessage("✅ Diagram and description sent to Proposal Writer!");
      setTimeout(() => setStatusMessage(""), STATUS_MESSAGE_TIMEOUT);
    } catch (error) {
      console.error("Error sending to proposal:", error);
      setStatusMessage("❌ Error sending to proposal: " + error.message);
      setTimeout(() => setStatusMessage(""), STATUS_MESSAGE_TIMEOUT);
    }
  };

  // Open print options modal
  const handlePrintClick = () => {
    setShowPrintModal(true);
  };
  
  // Execute print with selected options
  const executePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setStatusMessage("⚠️ Please allow popups to print");
      setTimeout(() => setStatusMessage(""), 3000);
      return;
    }
    
    // Validate exported data is a safe data URL
    const isSafeDataUrl = exportedData && (
      exportedData.startsWith('data:image/png;base64,') || 
      exportedData.startsWith('data:image/svg+xml;base64,')
    );
    
    // Escape user inputs for HTML safety
    const safeArchName = escapeHtml(architectureName);
    const safeClientName = escapeHtml(tender?.clientName || 'Project');
    const safeTitle = escapeHtml(tender?.title || '');
    const safeDescription = escapeHtml(systemDescription);
    
    // Create print-friendly HTML based on options
    const diagramHtml = printOptions.includeDiagram ? `
      <div class="diagram-page">
        <h1 style="margin: 0 0 5px 0; color: #1e293b;">📐 ${safeArchName}</h1>
        <p style="color: #64748b; font-size: 14px; margin-bottom: 20px;">${safeClientName} - ${safeTitle}</p>
        <div class="diagram-container">
          ${isSafeDataUrl ? `<img src="${exportedData}" style="max-width: 100%; height: auto;" />` : '<p>Export diagram first to print</p>'}
        </div>
      </div>
    ` : '';
    
    const descriptionHtml = (printOptions.includeDescription && systemDescription) ? `
      <div class="description-page">
        <h1 style="margin: 0 0 5px 0; color: #1e293b;">📄 System Description</h1>
        <p style="color: #64748b; font-size: 14px; margin-bottom: 20px;">${safeArchName} - ${safeClientName}</p>
        <div class="description-container">
          <pre>${safeDescription}</pre>
        </div>
      </div>
    ` : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeArchName} - ${safeClientName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .diagram-page { padding: 20px; }
            .diagram-container { 
              border: 1px solid #e2e8f0; 
              border-radius: 8px; 
              padding: 20px; 
              position: relative;
              min-height: 400px;
            }
            .description-page { padding: 20px; }
            .description-container { 
              padding: 20px; 
              background: #f8fafc; 
              border-radius: 8px; 
              border: 1px solid #e2e8f0; 
            }
            .description-container pre { 
              white-space: pre-wrap; 
              font-family: inherit; 
              line-height: 1.6; 
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .diagram-page { 
                page-break-after: always; 
                page-break-inside: avoid;
              }
              .description-page { 
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${diagramHtml}
          ${descriptionHtml}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); }
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setShowPrintModal(false);
  };

  // Handle export for printing
  const handleExportForPrint = () => {
    if (drawioRef.current) {
      drawioRef.current.exportDiagram({ format: 'png' });
    }
  };

  if (loading) {
    return (
      <div className="sld-container">
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          Loading System Architecture...
        </div>
      </div>
    );
  }

  return (
    <div className="sld-container">
      {/* Header */}
      <div className="sld-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
            <span style={{ fontSize: "24px" }}>📐</span>
            <input
              type="text"
              value={architectureName}
              onChange={(e) => setArchitectureName(e.target.value)}
              style={{
                fontSize: "20px",
                fontWeight: "600",
                border: "1px solid transparent",
                borderRadius: "4px",
                padding: "4px 8px",
                background: "transparent",
                color: "#1e293b",
                minWidth: "200px",
                maxWidth: "400px",
              }}
              onFocus={(e) => e.target.style.borderColor = "#2563eb"}
              onBlur={(e) => e.target.style.borderColor = "transparent"}
              placeholder="Enter Architecture Name"
            />
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px", marginLeft: "34px" }}>
            Full-featured diagrams.net editor with all drawing tools
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <button 
            className="btn-primary" 
            onClick={saveDiagram}
            disabled={saving || !editorReady}
            title={!editorReady ? "Editor is loading..." : "Save diagram to database"}
          >
            💾 {saving ? "Saving..." : !editorReady ? "Loading..." : "Save"}
          </button>
          <button 
            className="btn-insert" 
            onClick={generateSystemDescription}
            disabled={isGeneratingAI}
            style={{ background: "#8b5cf6", color: "white", borderColor: "#8b5cf6" }}
          >
            🤖 {isGeneratingAI ? "Generating..." : "Generate Description"}
          </button>
          {systemDescription && (
            <button 
              className="btn-secondary" 
              onClick={() => setShowSystemDescription(!showSystemDescription)}
            >
              📄 {showSystemDescription ? "Hide" : "View"}
            </button>
          )}
          <button 
            className="btn-primary" 
            onClick={handleSendToProposal}
            style={{ background: "#059669", borderColor: "#059669" }}
            title="Send diagram and description to Proposal Writer"
          >
            📤 Send to Proposal
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div style={{ 
          padding: "10px 15px", 
          background: statusMessage.includes("❌") ? "#fef2f2" : statusMessage.includes("⚠️") ? "#fef3c7" : "#ecfdf5",
          color: statusMessage.includes("❌") ? "#dc2626" : statusMessage.includes("⚠️") ? "#d97706" : "#059669",
          borderRadius: "6px", 
          marginBottom: "15px",
          fontSize: "14px"
        }}>
          {statusMessage}
        </div>
      )}
      
      {/* PNG Export Status - Visible UI State */}
      {pngExportStatus && (
        <div style={{ 
          padding: "10px 15px", 
          background: pngExportStatus === "generating" ? "#eff6ff" : pngExportStatus === "success" ? "#ecfdf5" : "#fef2f2",
          color: pngExportStatus === "generating" ? "#1e40af" : pngExportStatus === "success" ? "#059669" : "#dc2626",
          borderRadius: "6px", 
          marginBottom: "15px",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <span>
            {pngExportStatus === "generating" && `⏳ Generating diagram image (attempt ${pngExportAttempts}/${PNG_EXPORT_MAX_ATTEMPTS})...`}
            {pngExportStatus === "success" && "✅ Diagram image generated successfully"}
            {pngExportStatus === "failed" && "⚠️ Image generation failed - diagram saved with XML only"}
          </span>
          {pngExportStatus === "failed" && (
            <button 
              className="btn-secondary" 
              onClick={handleSendToProposal}
              style={{ marginLeft: "10px", padding: "4px 12px", fontSize: "13px" }}
            >
              🔄 Retry Export
            </button>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ marginBottom: "20px" }}>
        {/* Diagrams.net Editor */}
        <div style={{ 
          border: "2px solid #e2e8f0", 
          borderRadius: "8px", 
          overflow: "hidden",
          height: "700px"
        }}>
          <DrawIoEmbed
            key={`diagram-${tenderId}-${diagramXml ? 'loaded' : 'empty'}`}
            ref={drawioRef}
            xml={diagramXml}
            urlParameters={DRAWIO_CONFIG}
            onLoad={handleEditorLoad}
            onExport={handleExport}
          />
        </div>
      </div>

      {/* System Description Panel */}
      {showSystemDescription && systemDescription && (
        <div className="sld-description-panel">
          <div className="sld-description-header">
            <h3 style={{ margin: 0 }}>📄 AI-Generated System Description</h3>
            <button 
              className="sld-details-close"
              onClick={() => setShowSystemDescription(false)}
            >
              ×
            </button>
          </div>
          <div className="sld-description-content">
            <div 
              style={{ 
                whiteSpace: "pre-wrap", 
                fontFamily: "inherit", 
                margin: 0,
                lineHeight: "1.8",
                fontSize: "14px"
              }}
              dangerouslySetInnerHTML={{ 
                __html: (() => {
                  // Escape HTML to prevent XSS (since content comes from AI API)
                  const escapeHtml = (text) => {
                    const div = document.createElement('div');
                    div.textContent = text;
                    return div.innerHTML;
                  };
                  
                  const escaped = escapeHtml(systemDescription);
                  
                  // Apply markdown-like formatting safely
                  return escaped
                    .replace(/\*\*([^\*\n]+)\*\*/g, '<strong>$1</strong>') // Bold (no line breaks)
                    .replace(/^### (.+)$/gm, '<h3 style="margin-top: 20px; margin-bottom: 10px; font-size: 18px; color: #1e293b;">$1</h3>') // H3
                    .replace(/^## (.+)$/gm, '<h2 style="margin-top: 24px; margin-bottom: 12px; font-size: 20px; color: #1e293b;">$1</h2>') // H2
                    .replace(/^# (.+)$/gm, '<h1 style="margin-top: 28px; margin-bottom: 14px; font-size: 24px; color: #1e293b;">$1</h1>') // H1
                    .replace(/^- (.+)$/gm, '<div style="margin-left: 20px; margin-bottom: 6px;">• $1</div>') // Bullet points
                    .replace(/^(\d+)\. (.+)$/gm, '<div style="margin-left: 20px; margin-bottom: 6px;">$1. $2</div>') // Numbered lists (keep numbers)
                    .replace(/\n\n/g, '<br/><br/>') // Paragraphs
                    .replace(/\n/g, '<br/>'); // Line breaks
                })()
              }}
            />
          </div>
          <div className="sld-description-actions">
            <button 
              className="btn-primary"
              onClick={() => {
                navigator.clipboard.writeText(systemDescription);
                setStatusMessage("📋 Copied to clipboard!");
                setTimeout(() => setStatusMessage(""), 2000);
              }}
            >
              📋 Copy to Clipboard
            </button>
            <button 
              className="btn-secondary"
              onClick={generateSystemDescription}
              disabled={isGeneratingAI}
            >
              🔄 Regenerate
            </button>
          </div>
        </div>
      )}

      {/* Print Options Modal */}
      {showPrintModal && (
        <div className="sld-modal-overlay" onClick={() => setShowPrintModal(false)}>
          <div className="sld-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
            <div className="sld-modal-header">
              <h3>🖨️ Print Options</h3>
              <button className="sld-modal-close" onClick={() => setShowPrintModal(false)}>×</button>
            </div>
            <div className="sld-modal-body">
              <p style={{ color: "#64748b", fontSize: "13px", marginBottom: "15px" }}>
                Select what to include in the print output:
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={printOptions.includeDiagram}
                    onChange={(e) => setPrintOptions({ ...printOptions, includeDiagram: e.target.checked })}
                    style={{ width: "18px", height: "18px" }}
                  />
                  <div>
                    <div style={{ fontWeight: "500" }}>📐 Diagram</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>System architecture diagram</div>
                  </div>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={printOptions.includeDescription}
                    onChange={(e) => setPrintOptions({ ...printOptions, includeDescription: e.target.checked })}
                    disabled={!systemDescription}
                    style={{ width: "18px", height: "18px" }}
                  />
                  <div>
                    <div style={{ fontWeight: "500", color: systemDescription ? "#1e293b" : "#94a3b8" }}>📄 System Description</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      {systemDescription ? "AI-generated system description" : "Generate description first"}
                    </div>
                  </div>
                </label>
              </div>
              {printOptions.includeDiagram && printOptions.includeDescription && (
                <div style={{ 
                  marginTop: "15px", 
                  padding: "10px", 
                  background: "#f0f9ff", 
                  borderRadius: "6px", 
                  fontSize: "12px", 
                  color: "#0369a1" 
                }}>
                  ℹ️ Diagram and Description will print on separate pages
                </div>
              )}
            </div>
            <div className="sld-modal-footer">
              <button className="btn-secondary" onClick={() => setShowPrintModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={executePrint}
                disabled={!printOptions.includeDiagram && !printOptions.includeDescription}
              >
                🖨️ Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
